import { reminderMessages } from '@common/intl'
import type { CaseReminderDueSubscription } from '@gql'
import { caseDetailUrl, caseRemindersUrl } from '@sections/cases/urls'
import type { IntlShape } from 'react-intl'

export type ReminderDueEvent = CaseReminderDueSubscription['caseReminderDue']

export interface ReminderDueAlert {
  title: string
  body: string
  /** Where the toast button and the notification click go. */
  url: string
  actionLabel: string
  /** OS notification tag: a repeat of the same alert replaces the old one. */
  tag: string
}

/** Drops repeats of the same reminder (the push is at-least-once), keeping the first. */
export const uniqueReminderEvents = (events: readonly ReminderDueEvent[]): ReminderDueEvent[] => {
  const seen = new Set<string>()
  return events.filter((event) => {
    if (seen.has(event.reminderId)) {
      return false
    }

    seen.add(event.reminderId)
    return true
  })
}

/**
 * One alert for a burst of due reminders: one reminder names its case and
 * opens it; several are counted and open the Reminders page. Null for none.
 */
export const buildReminderDueAlert = (
  events: readonly ReminderDueEvent[],
  intl: IntlShape,
  formatTime: (dueAt: string) => string,
): ReminderDueAlert | null => {
  const unique = uniqueReminderEvents(events)
  if (unique.length === 0) {
    return null
  }

  if (unique.length === 1) {
    const [event] = unique
    return {
      actionLabel: intl.formatMessage(reminderMessages.openCase),
      body: intl.formatMessage(reminderMessages.dueBody, { time: formatTime(event.dueAt) }),
      tag: `dolpheen-reminder-${event.reminderId}`,
      title: intl.formatMessage(reminderMessages.dueTitle, { code: event.caseCode }),
      url: caseDetailUrl(undefined, event.caseId),
    }
  }

  return {
    actionLabel: intl.formatMessage(reminderMessages.viewReminders),
    body: intl.formatMessage(reminderMessages.dueBurstBody),
    tag: 'dolpheen-reminders-batch',
    title: intl.formatMessage(reminderMessages.dueBurstTitle, { count: unique.length }),
    url: caseRemindersUrl(),
  }
}
